import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  FileUp,
  Hash,
  KeyRound,
  Loader2,
  Mail,
  MoreVertical,
  Pause,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
  RotateCcw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useExclusiveSelect } from '../../hooks/useExclusiveSelect';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Card, CardHeader, CardTitle } from '../../components/ui/card';
import { subscribersApi } from '../../../api/subscribersApi';
import { subscriberScheduleApi } from '../../../api/subscriberScheduleApi';
import { topicsApi } from '../../../api/topicsApi';
import { buzzwordsApi } from '../../../api/buzzwordsApi';
import { keywordsApi } from '../../../api/keywordsApi';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import {
  LANGUAGE_OPTIONS,
  LanguageFlagByCode,
  type LanguageCode,
} from '../../components/LanguageFlag';

/* ═══════════════════ Types ═══════════════════ */

const LANGUAGE_CODES = Object.keys(LANGUAGE_OPTIONS) as LanguageCode[];

type ApiSubscriber = {
  id: number;
  mail: string;
  username: string;
  client_id?: number | null;
  language?: LanguageCode | string | null;
  last_sent_at?: string | null;
};

type Topic = {
  id: number;
  name: string;
  buzzwords: string[];
  keywords: string[];
  color: string;
};

type ToastApi = {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
};

/* ═══════════════════ Constants ═══════════════════ */

const AVATAR_COLORS = [
  '#16a34a', '#2563eb', '#ea580c', '#7c3aed', '#0891b2',
  '#dc2626', '#ca8a04', '#0d9488', '#6366f1', '#e11d48',
];

const TOPIC_COLOR_PALETTE = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-sky-100 text-sky-700 border-sky-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-amber-100 text-amber-700 border-amber-200',
];

const KEYWORD_COLORS = [
  'bg-sky-50 text-sky-600 border-sky-100',
  'bg-teal-50 text-teal-600 border-teal-100',
  'bg-blue-50 text-blue-600 border-blue-100',
  'bg-cyan-50 text-cyan-600 border-cyan-100',
  'bg-indigo-50 text-indigo-600 border-indigo-100',
  'bg-emerald-50 text-emerald-600 border-emerald-100',
  'bg-violet-50 text-violet-600 border-violet-100',
];

const BUZZWORD_COLORS = [
  'bg-indigo-50 text-indigo-600 border-indigo-100',
  'bg-emerald-50 text-emerald-600 border-emerald-100',
  'bg-violet-50 text-violet-600 border-violet-100',
  'bg-cyan-50 text-cyan-600 border-cyan-100',
  'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100',
  'bg-amber-50 text-amber-600 border-amber-100',
  'bg-teal-50 text-teal-600 border-teal-100',
];

const LIQUID_GLASS_PANEL =
  'border border-white/55 bg-white/35 shadow-[0_18px_60px_-32px_rgba(30,64,175,0.45)] ring-1 ring-white/35 backdrop-blur-2xl backdrop-saturate-150  rounded-2xl';
const LIQUID_GLASS_CARD =
  'border border-white/55 bg-white/50 shadow-[0_16px_44px_-30px_rgba(15,23,42,0.42)] ring-1 ring-white/45 backdrop-blur-xl backdrop-saturate-150';
const LIQUID_GLASS_SOFT =
  'border border-white/45 bg-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] backdrop-blur-md rounded-2xl';
const GLASS_FIELD =
  'rounded-xl border-white/55 bg-white/45 text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] ring-1 ring-white/40 backdrop-blur-md transition-colors placeholder:text-slate-400/90 hover:bg-white/55 focus-visible:border-white/70 focus-visible:ring-blue-200/50';

const COLOR_TOKEN_PALETTE: Record<string, string> = {
  blue:   'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  green:  'bg-green-100 text-green-700 border-green-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  rose:   'bg-rose-100 text-rose-700 border-rose-200',
  cyan:   'bg-cyan-100 text-cyan-700 border-cyan-200',
};

type ApiTopicRow = {
  id: number;
  topic?: string;
  name?: string;
  buzzwords?: Array<string | ApiWordEntry>;
  keywords?: Array<string | ApiWordEntry>;
  color?: string;
};

const TOPIC_COLOR_TOKENS = ['blue', 'purple', 'green', 'orange', 'rose', 'cyan'] as const;

/* ═══════════════════ Helpers ═══════════════════ */

function topicColor(apiColor: unknown, fallbackIdx: number): string {
  if (typeof apiColor === 'string' && apiColor.trim()) {
    if (apiColor.includes('bg-')) return apiColor;
    if (COLOR_TOKEN_PALETTE[apiColor]) return COLOR_TOKEN_PALETTE[apiColor];
  }
  return TOPIC_COLOR_PALETTE[fallbackIdx % TOPIC_COLOR_PALETTE.length];
}

function avatarBg(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string, email: string): string {
  const n = name?.trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  return (email || '??').slice(0, 2).toUpperCase();
}

function normalizeLanguageCode(value: unknown): LanguageCode {
  const raw = String(value ?? 'de').toLowerCase();
  if (LANGUAGE_CODES.includes(raw as LanguageCode)) return raw as LanguageCode;
  return 'de';
}

function langLabel(code: string | null | undefined): string {
  return normalizeLanguageCode(code).toUpperCase();
}

function tzAbbr(tz: string | null | undefined): string {
  if (!tz) return 'CET';
  const map: Record<string, string> = {
    'Europe/Berlin': 'CET', 'Europe/London': 'GMT', 'UTC': 'UTC',
    'America/New_York': 'EST', 'America/Los_Angeles': 'PST',
  };
  return map[tz] || tz;
}

function formatLastSent(value: string | null | undefined, localeTag: string, neverLabel: string): string {
  if (!value) return neverLabel;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return neverLabel;
  return d.toLocaleDateString(localeTag, { year: 'numeric', month: 'short', day: 'numeric' });
}

function subscriberApiError(error: unknown): string {
  const ax = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string }) => x?.msg).filter(Boolean).join(', ') || 'Request failed';
  }
  return ax.message || 'Request failed';
}

function getPageNumbers(current: number, total: number): (number | 'dots')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'dots')[] = [1];
  if (current > 3) pages.push('dots');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push('dots');
  if (total > 1) pages.push(total);
  return pages;
}

type ApiWordEntry = {
  id?: number;
  word?: string;
  buzzword?: string;
  keyword?: string;
};

function parseWordEntries(
  entries: Array<string | ApiWordEntry> | undefined,
  wordField: 'buzzword' | 'keyword',
): { words: string[]; idMap: Record<string, number> } {
  const words: string[] = [];
  const idMap: Record<string, number> = {};

  if (!Array.isArray(entries)) return { words, idMap };

  for (const entry of entries) {
    if (typeof entry === 'string') {
      if (entry.trim()) words.push(entry.trim());
      continue;
    }

    const word = (
      entry.word
      ?? (wordField === 'buzzword' ? entry.buzzword : entry.keyword)
      ?? ''
    ).trim();
    if (!word) continue;
    words.push(word);
    if (entry.id != null) idMap[word] = entry.id;
  }

  return { words, idMap };
}

function parseSubscriberTopics(body: ApiTopicRow[]): {
  topics: Topic[];
  buzzwordIdMap: Record<number, Record<string, number>>;
  keywordIdMap: Record<number, Record<string, number>>;
} {
  const buzzwordIdMap: Record<number, Record<string, number>> = {};
  const keywordIdMap: Record<number, Record<string, number>> = {};

  const topics = body.map((t, idx) => {
    const buzzwordData = parseWordEntries(t.buzzwords, 'buzzword');
    const keywordData = parseWordEntries(t.keywords, 'keyword');
    buzzwordIdMap[t.id] = buzzwordData.idMap;
    keywordIdMap[t.id] = keywordData.idMap;

    return {
      id: t.id,
      name: (t.topic ?? t.name ?? '') as string,
      buzzwords: buzzwordData.words,
      keywords: keywordData.words,
      color: topicColor(t.color, idx),
    };
  });

  return { topics, buzzwordIdMap, keywordIdMap };
}

function buzzwordFieldKey(subscriberId: number, topicId: number): string {
  return `buzz-${subscriberId}-${topicId}`;
}

function keywordFieldKey(subscriberId: number, topicId: number): string {
  return `kw-${subscriberId}-${topicId}`;
}

const PER_PAGE_OPTIONS = [10, 25, 50];

const pad2 = (n: number) => String(n).padStart(2, '0');

/* ═══════════════════ Component ═══════════════════ */

export function SubscribersPage() {
  const navigate = useNavigate();
  const { t, localeTag } = useUiLocale();
  const { showSuccess, showError } = useToast() as ToastApi;

  /* ── Core data ── */
  const [subscribers, setSubscribers] = useState<ApiSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [schedules, setSchedules] = useState<
    Record<number, { hour: string; minute: string; timezone?: string | null }>
  >({});
  const [topicsBySub, setTopicsBySub] = useState<Record<number, Topic[]>>({});
  const [buzzwordIdMapBySub, setBuzzwordIdMapBySub] = useState<
    Record<number, Record<number, Record<string, number>>>
  >({});
  const [keywordIdMapBySub, setKeywordIdMapBySub] = useState<
    Record<number, Record<number, Record<string, number>>>
  >({});
  const [newBuzzwordByTopicKey, setNewBuzzwordByTopicKey] = useState<Record<string, string>>({});
  const [newKeywordByTopicKey, setNewKeywordByTopicKey] = useState<Record<string, string>>({});
  const [newSidebarTopicName, setNewSidebarTopicName] = useState('');
  const [wordsSaving, setWordsSaving] = useState(false);

  /* ── Per-row topic/buzzword CSV/XLSX import ── */
  const [importingSubId, setImportingSubId] = useState<number | null>(null);
  // A single hidden <input> is shared by all rows; the ref tracks which subscriber
  // the in-flight file pick belongs to (cleaner than rendering N inputs in the table).
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingImportSubIdRef = useRef<number | null>(null);

  /* ── Sidebar ── */
  const [selectedSub, setSelectedSub] = useState<ApiSubscriber | null>(null);

  /* ── Filters ── */
  const [filterLang, setFilterLang] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [filterSchedule, setFilterSchedule] = useState('all');

  const {
    openId: openFilterSelect,
    setOpenId: setOpenFilterSelect,
    onOpenChange: onFilterOpenChange,
    onSelectContentOutside: onFilterSelectContentOutside,
    onTriggerPointerDown: onFilterTriggerPointerDown,
    exclusiveSelectGroupId: filterSelectGroupId,
    exclusiveSelectTriggerClass: filterSelectTriggerClass,
  } = useExclusiveSelect<'language' | 'status' | 'topic' | 'schedule'>('subscribers-filters');

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  /* ── Edit dialog ── */
  const [editTarget, setEditTarget] = useState<ApiSubscriber | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editNewPassword, setEditNewPassword] = useState('');
  const [editPasswordVisible, setEditPasswordVisible] = useState(false);
  const [editLanguage, setEditLanguage] = useState<LanguageCode>('de');
  const [saving, setSaving] = useState(false);

  /* ── Delete dialog ── */
  const [deleteTarget, setDeleteTarget] = useState<ApiSubscriber | null>(null);

  /* ── Add dialog ── */
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [newLanguage, setNewLanguage] = useState<LanguageCode>('de');

  /* ═══════════ Data Loading ═══════════ */

  const loadSubscribers = useCallback(async (searchQuery = '') => {
    setLoading(true);
    try {
      const params: { limit: number; search?: string } = { limit: 200 };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const { data } = await subscribersApi.list(params);
      const list = Array.isArray(data) ? data : [];
      setSubscribers(list);

      const scheduleResults = await Promise.allSettled(
        list.map((s) => subscriberScheduleApi.get(s.id).then((r) => [s.id, r.data] as const)),
      );
      const topicResults = await Promise.allSettled(
        list.map((s) => topicsApi.list(s.id).then((r) => [s.id, r.data] as const)),
      );

      const nextSchedules: typeof schedules = {};
      scheduleResults.forEach((r) => {
        if (r.status === 'fulfilled') {
          const [id, body] = r.value as unknown as [
            number,
            { hour?: number; minute?: number; timezone?: string | null } | null,
          ];
          if (body && typeof body.hour === 'number' && typeof body.minute === 'number') {
            nextSchedules[id] = { hour: pad2(body.hour), minute: pad2(body.minute), timezone: typeof body.timezone === 'string' ? body.timezone : null };
          }
        }
      });
      setSchedules(nextSchedules);

      const nextTopics: Record<number, Topic[]> = {};
      const nextBuzzwordMaps: typeof buzzwordIdMapBySub = {};
      const nextKeywordMaps: typeof keywordIdMapBySub = {};
      topicResults.forEach((r) => {
        if (r.status === 'fulfilled') {
          const [id, body] = r.value as unknown as [number, ApiTopicRow[] | null];
          if (Array.isArray(body)) {
            const parsed = parseSubscriberTopics(body);
            nextTopics[id] = parsed.topics;
            nextBuzzwordMaps[id] = parsed.buzzwordIdMap;
            nextKeywordMaps[id] = parsed.keywordIdMap;
          }
        }
      });
      setTopicsBySub(nextTopics);
      setBuzzwordIdMapBySub(nextBuzzwordMaps);
      setKeywordIdMapBySub(nextKeywordMaps);
    } catch {
      setSubscribers([]);
      setSchedules({});
      setTopicsBySub({});
      setBuzzwordIdMapBySub({});
      setKeywordIdMapBySub({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadSubscribers(); }, [loadSubscribers]);

  useEffect(() => {
    const timer = setTimeout(() => { void loadSubscribers(search); }, 400);
    return () => clearTimeout(timer);
  }, [search, loadSubscribers]);

  /* ═══════════ Derived Values ═══════════ */

  const getStatus = useCallback(
    (id: number): 'Active' | 'Inactive' => (schedules[id] ? 'Active' : 'Inactive'),
    [schedules],
  );

  const allTopicNames = useMemo(() => {
    const names = new Set<string>();
    Object.values(topicsBySub).forEach((ts) => ts.forEach((t) => { if (t.name) names.add(t.name); }));
    return [...names].sort();
  }, [topicsBySub]);

  const filtered = useMemo(() => {
    let list = subscribers;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((s) => s.mail.toLowerCase().includes(q) || s.username?.toLowerCase().includes(q));
    if (filterLang !== 'all') list = list.filter((s) => (s.language || 'de').toLowerCase() === filterLang);
    if (filterStatus !== 'all') list = list.filter((s) => getStatus(s.id).toLowerCase() === filterStatus);
    if (filterTopic !== 'all') list = list.filter((s) => (topicsBySub[s.id] ?? []).some((t) => t.name === filterTopic));
    if (filterSchedule !== 'all') {
      list = filterSchedule === 'scheduled'
        ? list.filter((s) => !!schedules[s.id])
        : list.filter((s) => !schedules[s.id]);
    }
    return list;
  }, [subscribers, search, filterLang, filterStatus, filterTopic, filterSchedule, getStatus, topicsBySub, schedules]);

  const stats = useMemo(() => ({
    total: subscribers.length,
    active: subscribers.filter((s) => getStatus(s.id) === 'Active').length,
    scheduled: Object.keys(schedules).length,
    issues: 0,
  }), [subscribers, schedules, getStatus]);

  /* Pagination */
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const paginatedList = filtered.slice((safePage - 1) * perPage, safePage * perPage);
  const pageNumbers = getPageNumbers(safePage, totalPages);
  const showStart = filtered.length === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showEnd = Math.min(safePage * perPage, filtered.length);

  useEffect(() => { setPage(1); }, [search, filterLang, filterStatus, filterTopic, filterSchedule, perPage]);

  const resetFilters = () => {
    setSearch('');
    setFilterLang('all');
    setFilterStatus('all');
    setFilterTopic('all');
    setFilterSchedule('all');
  };

  /* Sidebar-derived data */
  const selectedSubLive = useMemo(
    () => selectedSub ? subscribers.find((s) => s.id === selectedSub.id) ?? selectedSub : null,
    [selectedSub, subscribers],
  );
  const sidebarTopics = selectedSubLive ? (topicsBySub[selectedSubLive.id] ?? []) : [];
  const sidebarSchedule = selectedSubLive ? schedules[selectedSubLive.id] ?? null : null;

  /* ═══════════ Topic / word handlers ═══════════ */

  const reloadSubscriberTopics = useCallback(async (subId: number) => {
    try {
      const { data } = await topicsApi.list(subId);
      if (!Array.isArray(data)) return;
      const parsed = parseSubscriberTopics(data as ApiTopicRow[]);
      setTopicsBySub((prev) => ({ ...prev, [subId]: parsed.topics }));
      setBuzzwordIdMapBySub((prev) => ({ ...prev, [subId]: parsed.buzzwordIdMap }));
      setKeywordIdMapBySub((prev) => ({ ...prev, [subId]: parsed.keywordIdMap }));
    } catch {
      /* keep current state */
    }
  }, []);

  const handleImportClick = (subId: number) => {
    if (importingSubId !== null) return; // ignore while any row is importing
    pendingImportSubIdRef.current = subId;
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // allow re-picking the same filename
      fileInputRef.current.click();
    }
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const subId = pendingImportSubIdRef.current;
    e.target.value = '';
    if (!file || subId == null) return;

    setImportingSubId(subId);
    try {
      const { data } = await topicsApi.importCsv(subId, file);
      const created = data?.created_topics ?? 0;
      const merged = data?.merged_topics ?? 0;
      const buzz = data?.created_buzzwords ?? 0;
      if (created === 0 && merged === 0 && buzz === 0) {
        showSuccess(t('subscribers.toastImportNothing'));
      } else {
        showSuccess(
          t('subscribers.toastImportSuccess', { created, merged, buzz }),
        );
      }
      await reloadSubscriberTopics(subId); // refresh row badges + sidebar
    } catch (err) {
      showError(subscriberApiError(err)); // surfaces backend 400/403 detail
    } finally {
      setImportingSubId(null);
      pendingImportSubIdRef.current = null;
    }
  };

  const handleAddBuzzword = async (subId: number, topicId: number) => {
    const fieldKey = buzzwordFieldKey(subId, topicId);
    const word = (newBuzzwordByTopicKey[fieldKey] ?? '').trim();
    if (!word) return;

    setTopicsBySub((prev) => ({
      ...prev,
      [subId]: (prev[subId] ?? []).map((topic) =>
        topic.id === topicId && !topic.buzzwords.includes(word)
          ? { ...topic, buzzwords: [...topic.buzzwords, word] }
          : topic,
      ),
    }));
    setNewBuzzwordByTopicKey((prev) => ({ ...prev, [fieldKey]: '' }));

    setWordsSaving(true);
    try {
      const res = await buzzwordsApi.add(subId, topicId, { buzzword: word });
      const newId = (res.data as { id?: number })?.id;
      if (newId) {
        setBuzzwordIdMapBySub((prev) => ({
          ...prev,
          [subId]: {
            ...(prev[subId] ?? {}),
            [topicId]: { ...(prev[subId]?.[topicId] ?? {}), [word]: newId },
          },
        }));
      }
      showSuccess(t('subscribers.toastBuzzwordAdded'));
    } catch (error) {
      showError(subscriberApiError(error));
      await reloadSubscriberTopics(subId);
    } finally {
      setWordsSaving(false);
    }
  };

  const handleRemoveBuzzword = async (subId: number, topicId: number, word: string) => {
    setTopicsBySub((prev) => ({
      ...prev,
      [subId]: (prev[subId] ?? []).map((topic) =>
        topic.id === topicId
          ? { ...topic, buzzwords: topic.buzzwords.filter((item) => item !== word) }
          : topic,
      ),
    }));

    const buzzwordId = buzzwordIdMapBySub[subId]?.[topicId]?.[word];
    if (buzzwordId == null) return;

    setWordsSaving(true);
    try {
      await buzzwordsApi.remove(subId, topicId, buzzwordId);
      showSuccess(t('subscribers.toastBuzzwordRemoved'));
    } catch (error) {
      showError(subscriberApiError(error));
      await reloadSubscriberTopics(subId);
    } finally {
      setWordsSaving(false);
    }
  };

  const handleAddKeyword = async (subId: number, topicId: number) => {
    const fieldKey = keywordFieldKey(subId, topicId);
    const word = (newKeywordByTopicKey[fieldKey] ?? '').trim();
    if (!word) return;

    setTopicsBySub((prev) => ({
      ...prev,
      [subId]: (prev[subId] ?? []).map((topic) =>
        topic.id === topicId && !topic.keywords.includes(word)
          ? { ...topic, keywords: [...topic.keywords, word] }
          : topic,
      ),
    }));
    setNewKeywordByTopicKey((prev) => ({ ...prev, [fieldKey]: '' }));

    setWordsSaving(true);
    try {
      const res = await keywordsApi.add(subId, topicId, { keyword: word });
      const newId = (res.data as { id?: number })?.id;
      if (newId) {
        setKeywordIdMapBySub((prev) => ({
          ...prev,
          [subId]: {
            ...(prev[subId] ?? {}),
            [topicId]: { ...(prev[subId]?.[topicId] ?? {}), [word]: newId },
          },
        }));
      }
      showSuccess(t('subscribers.toastKeywordAdded'));
    } catch (error) {
      showError(subscriberApiError(error));
      await reloadSubscriberTopics(subId);
    } finally {
      setWordsSaving(false);
    }
  };

  const handleRemoveKeyword = async (subId: number, topicId: number, word: string) => {
    setTopicsBySub((prev) => ({
      ...prev,
      [subId]: (prev[subId] ?? []).map((topic) =>
        topic.id === topicId
          ? { ...topic, keywords: topic.keywords.filter((item) => item !== word) }
          : topic,
      ),
    }));

    const keywordId = keywordIdMapBySub[subId]?.[topicId]?.[word];
    if (keywordId == null) return;

    setWordsSaving(true);
    try {
      await keywordsApi.remove(subId, topicId, keywordId);
      showSuccess(t('subscribers.toastKeywordRemoved'));
    } catch (error) {
      showError(subscriberApiError(error));
      await reloadSubscriberTopics(subId);
    } finally {
      setWordsSaving(false);
    }
  };

  const handleAddSidebarTopic = async (subId: number) => {
    const name = newSidebarTopicName.trim();
    if (!name) return;

    const existing = topicsBySub[subId] ?? [];
    if (existing.some((topic) => topic.name.toLowerCase() === name.toLowerCase())) {
      showError(t('subscribers.toastTopicExists'));
      return;
    }

    setWordsSaving(true);
    try {
      await topicsApi.create(subId, {
        topic: name,
        color: TOPIC_COLOR_TOKENS[existing.length % TOPIC_COLOR_TOKENS.length],
      });
      setNewSidebarTopicName('');
      showSuccess(t('subscribers.toastTopicAdded'));
      await reloadSubscriberTopics(subId);
    } catch (error) {
      showError(subscriberApiError(error));
    } finally {
      setWordsSaving(false);
    }
  };

  const handleSelectRow = (s: ApiSubscriber) => {
    setSelectedSub((prev) => (prev?.id === s.id ? null : s));
  };

  const openEdit = (s: ApiSubscriber) => {
    setEditTarget(s);
    setEditEmail(s.mail);
    setEditUsername(s.username);
    setEditLanguage(normalizeLanguageCode(s.language));
    setEditNewPassword('');
    setEditPasswordVisible(false);
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const mail = editEmail.trim();
    const username = editUsername.trim();
    if (!mail || !username) { showError(t('subscribers.errEmailUsernameRequired')); return; }
    const pw = editNewPassword.trim();
    if (pw && pw.length < 8) { showError(t('subscribers.errPasswordMin')); return; }
    setSaving(true);
    try {
      await subscribersApi.update(editTarget.id, { mail, username, language: editLanguage });
      if (pw) await subscribersApi.setPassword(editTarget.id, { password: pw });
      showSuccess(t('subscribers.toastUpdated'));
      setEditTarget(null);
      await loadSubscribers();
    } catch (e) {
      showError(subscriberApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await subscribersApi.remove(deleteTarget.id);
      showSuccess(t('subscribers.toastRemoved'));
      if (selectedSub?.id === deleteTarget.id) setSelectedSub(null);
      setDeleteTarget(null);
      await loadSubscribers();
    } catch (e) {
      showError(subscriberApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    const mail = newEmail.trim();
    const username = newUsername.trim();
    const password = newPassword.trim();
    if (!mail || !username || !password) { showError(t('subscribers.errAllFieldsRequired')); return; }
    if (password.length < 8) { showError(t('subscribers.errPasswordMinCreate')); return; }
    setSaving(true);
    try {
      await subscribersApi.create({ mail, username, password, language: newLanguage });
      showSuccess(t('subscribers.toastAdded'));
      setNewEmail(''); setNewUsername(''); setNewPassword(''); setNewLanguage('de');
      setAddOpen(false);
      await loadSubscribers();
    } catch (e) {
      showError(subscriberApiError(e));
    } finally {
      setSaving(false);
    }
  };

  /* ═══════════ Render ═══════════ */

  return (
    <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-[rgb(224_231_255/0.55)]">
      {/* Shared hidden file input for per-row topic/buzzword import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx"
        className="hidden"
        onChange={handleImportFileChange}
      />

      {/* ─── Main content ─── */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className={`shrink-0 border-b border-white/40 px-6 py-5 ${LIQUID_GLASS_SOFT}`}>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-800 sm:text-3xl hawk-gradient-text">{t('subscribers.title')}</h1>
              <p className="text-sm text-slate-500 mt-0.5">{t('subscribers.subtitle')}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Button
                variant="outline"
                className={`gap-2 whitespace-nowrap border-white/50 bg-white/35 backdrop-blur-md hover:bg-white/55 ${LIQUID_GLASS_SOFT}`}
                onClick={() => showSuccess(t('subscribers.toastImportCsvSoon'))}
              >
                <Upload className="w-4 h-4" /> {t('subscribers.importCsv')}
              </Button>
              <Button
                className="gap-2 whitespace-nowrap bg-blue-600/90 text-white shadow-[0_8px_24px_-8px_rgba(37,99,235,0.55)] ring-1 ring-white/30 backdrop-blur-sm hover:bg-blue-700"
                onClick={() => setAddOpen(true)}
              >
                <UserPlus className="w-4 h-4" /> {t('subscribers.addSubscriber')}
              </Button>
            </div>
          </div>
        </header>

        {/* Body — fills viewport; table scrolls inside */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden px-6 py-5">
          {/* Stats */}
          <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 md:grid-cols-4">
            {([
              { icon: Users,        labelKey: 'subscribers.statTotal',  value: stats.total,     subKey: 'subscribers.statTotalSub',    pill: 'border-blue-200/80 text-blue-900',    iconWrap: 'bg-blue-100',    iconColor: 'text-blue-600',    valueClass: 'text-blue-900' },
              { icon: CheckCircle2, labelKey: 'subscribers.statActive', value: stats.active,    subKey: 'subscribers.statActiveSub',   pill: 'border-green-200/80 text-green-900',   iconWrap: 'bg-green-100',   iconColor: 'text-green-600',   valueClass: 'text-green-900' },
              { icon: Calendar,     labelKey: 'subscribers.statScheduled', value: stats.scheduled, subKey: 'subscribers.statScheduledSub', pill: 'border-emerald-200/80 text-emerald-900', iconWrap: 'bg-emerald-100', iconColor: 'text-emerald-600', valueClass: 'text-emerald-900' },
              { icon: AlertTriangle,labelKey: 'subscribers.statIssues', value: stats.issues,    subKey: 'subscribers.statIssuesSub',   pill: 'border-amber-200/80 text-amber-900',   iconWrap: 'bg-amber-100',   iconColor: 'text-amber-600',   valueClass: 'text-amber-900' },
            ] as const).map((card) => {
              const Icon = card.icon;
              return (
                <Card key={card.labelKey} className={`relative min-w-0 overflow-hidden rounded-2xl ${LIQUID_GLASS_CARD}`}>
                  <CardHeader className="relative flex flex-row items-center justify-between gap-3 space-y-0 px-4 py-4 sm:px-5 sm:py-5">
                    <div className="min-w-0 flex-1">
                      <CardTitle className={`hawk-outside-curve-pill inline-flex max-w-full border bg-white/55 px-2.5 py-0.5 text-[9px] font-semibold uppercase leading-tight tracking-wider backdrop-blur-sm sm:text-[10px] ${card.pill}`}>
                        {t(card.labelKey)}
                      </CardTitle>
                      <p className={`mt-1 text-3xl font-bold tabular-nums tracking-tight sm:text-4xl ${card.valueClass}`}>{loading ? '—' : card.value}</p>
                      <p className="mt-0.5 text-[11px] text-slate-400 truncate">{t(card.subKey)}</p>
                    </div>
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1 ring-white/50 backdrop-blur-sm sm:h-14 sm:w-14 ${card.iconWrap}`}>
                      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.iconColor}`} strokeWidth={2} />
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          {/* Table */}
          <div className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl ${LIQUID_GLASS_PANEL}`}>
            <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-3 border-b border-white/35 px-5 py-4 sm:px-6">
              <div className="flex shrink-0 items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 ring-1 ring-blue-200/50 ${LIQUID_GLASS_SOFT}`}
                >
                  <Users className="h-5 w-5 text-blue-600" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="whitespace-nowrap text-base font-bold tracking-tight hawk-gradient-text sm:text-lg">
                      {t('subscribers.tableTitle')}
                    </h2>
                    <span
                      className="hawk-outside-curve-pill inline-flex items-center rounded-full border border-blue-200/70 bg-white/50 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-blue-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-sm"
                    >
                      {loading ? '—' : filtered.length}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] font-medium text-slate-500/90">
                    {loading ? t('subscribers.loadingDirectory') : t('subscribers.shownOfTotal', { filtered: filtered.length, total: subscribers.length })}
                  </p>
                </div>
              </div>

              {/* Filters — same row, aligned right */}
              <div
                className={`ml-auto flex min-w-0 flex-2 flex-wrap items-end justify-end gap-2 rounded-2xl px-2.5 py-2 sm:gap-2.5 sm:px-3 ${LIQUID_GLASS_SOFT}`}
                data-exclusive-select-group={filterSelectGroupId}
              >
                <div className="relative w-full min-w-[240px] max-w-[400px] sm:w-[400px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500/80" />
                  <Input
                    placeholder={t('subscribers.searchPlaceholder')}
                    className={`h-10 w-full pl-9 ${GLASS_FIELD}`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="w-[148px] shrink-0 space-y-1 sm:w-[156px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500/90">{t('common.language')}</label>
                  <Select
                    value={filterLang}
                    open={openFilterSelect === 'language'}
                    onOpenChange={onFilterOpenChange('language')}
                    onValueChange={(value) => {
                      setFilterLang(value);
                      setOpenFilterSelect(null);
                    }}
                  >
                    <SelectTrigger
                      data-exclusive-select-id="language"
                      onPointerDown={onFilterTriggerPointerDown('language')}
                      className={`${filterSelectTriggerClass} h-10 w-full gap-2 ${GLASS_FIELD} [&>svg:last-child]:ml-auto [&>svg:last-child]:text-slate-400`}
                    >
                      {filterLang === 'all' ? (
                        <SelectValue placeholder={t('subscribers.filterAllLanguages')} />
                      ) : (
                        <span className="flex min-w-0 flex-1 items-center gap-1.5">
                          <LanguageFlagByCode code={filterLang as LanguageCode} className="h-3.5 w-3.5" />
                          <span className="truncate text-xs">{LANGUAGE_OPTIONS[filterLang as LanguageCode].countryLabel}</span>
                        </span>
                      )}
                    </SelectTrigger>
                    <SelectContent onPointerDownOutside={onFilterSelectContentOutside}>
                      <SelectItem value="all">{t('subscribers.filterAllLanguages')}</SelectItem>
                      {LANGUAGE_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          <span className="flex items-center gap-2">
                            <LanguageFlagByCode code={code} className="h-3.5 w-3.5" />
                            <span>{LANGUAGE_OPTIONS[code].countryLabel}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[112px] shrink-0 space-y-1 sm:w-[120px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500/90">{t('common.status')}</label>
                  <Select
                    value={filterStatus}
                    open={openFilterSelect === 'status'}
                    onOpenChange={onFilterOpenChange('status')}
                    onValueChange={(value) => {
                      setFilterStatus(value);
                      setOpenFilterSelect(null);
                    }}
                  >
                    <SelectTrigger
                      data-exclusive-select-id="status"
                      onPointerDown={onFilterTriggerPointerDown('status')}
                      className={`${filterSelectTriggerClass} h-10 w-full ${GLASS_FIELD}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent onPointerDownOutside={onFilterSelectContentOutside}>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value="active">{t('common.active')}</SelectItem>
                      <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[112px] shrink-0 space-y-1 sm:w-[120px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500/90">{t('common.topic')}</label>
                  <Select
                    value={filterTopic}
                    open={openFilterSelect === 'topic'}
                    onOpenChange={onFilterOpenChange('topic')}
                    onValueChange={(value) => {
                      setFilterTopic(value);
                      setOpenFilterSelect(null);
                    }}
                  >
                    <SelectTrigger
                      data-exclusive-select-id="topic"
                      onPointerDown={onFilterTriggerPointerDown('topic')}
                      className={`${filterSelectTriggerClass} h-10 w-full ${GLASS_FIELD}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent onPointerDownOutside={onFilterSelectContentOutside}>
                      <SelectItem value="all">{t('subscribers.filterAllTopics')}</SelectItem>
                      {allTopicNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[112px] shrink-0 space-y-1 sm:w-[120px]">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500/90">{t('common.schedule')}</label>
                  <Select
                    value={filterSchedule}
                    open={openFilterSelect === 'schedule'}
                    onOpenChange={onFilterOpenChange('schedule')}
                    onValueChange={(value) => {
                      setFilterSchedule(value);
                      setOpenFilterSelect(null);
                    }}
                  >
                    <SelectTrigger
                      data-exclusive-select-id="schedule"
                      onPointerDown={onFilterTriggerPointerDown('schedule')}
                      className={`${filterSelectTriggerClass} h-10 w-full ${GLASS_FIELD}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent onPointerDownOutside={onFilterSelectContentOutside}>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value="scheduled">{t('subscribers.filterScheduled')}</SelectItem>
                      <SelectItem value="none">{t('subscribers.filterNotScheduled')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className={`h-10 shrink-0 gap-1.5 whitespace-nowrap text-slate-600 hover:bg-white/55 hover:text-slate-800 ${GLASS_FIELD}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t('common.reset')}
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" /> {t('subscribers.loading')}
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/40 bg-white/25 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 backdrop-blur-md">
                      <th className="px-6 py-3">{t('common.subscriber')}</th>
                      <th className="px-4 py-3">{t('subscribers.colPreferences')}</th>
                      <th className="px-4 py-3">{t('subscribers.colDelivery')}</th>
                      <th className="px-4 py-3">{t('common.status')}</th>
                      <th className="px-4 py-3">{t('subscribers.colLastSent')}</th>
                      <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence initial={false}>
                      {paginatedList.map((s) => {
                        const subTopics = topicsBySub[s.id] ?? [];
                        const topicNames = subTopics.map((t) => t.name);
                        const allBuzz = [...new Set(subTopics.flatMap((t) => t.buzzwords))];
                        const allKeywords = [...new Set(subTopics.flatMap((t) => t.keywords))];
                        const sched = schedules[s.id] ?? null;
                        const status = getStatus(s.id);
                        const isSelected = selectedSub?.id === s.id;

                        return (
                          <motion.tr
                            key={s.id}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0 }}
                            onClick={() => handleSelectRow(s)}
                            className={`cursor-pointer border-b border-white/30 transition-colors hover:bg-white/40 ${isSelected ? 'bg-blue-100/35 ring-1 ring-inset ring-blue-200/40' : 'bg-white/10'}`}
                          >
                            {/* Subscriber */}
                            <td className="px-6 py-3" style={{ borderLeft: `4px solid ${avatarBg(s.id)}` }}>
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                                  style={{ backgroundColor: avatarBg(s.id) }}
                                >
                                  {getInitials(s.username, s.mail)}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-800 truncate">{s.username || s.mail}</span>
                                    <Badge variant="secondary" className="flex shrink-0 items-center gap-1 px-1.5 py-0 text-[10px] font-semibold">
                                      <LanguageFlagByCode code={normalizeLanguageCode(s.language)} className="h-3 w-3" />
                                      {langLabel(s.language)}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-slate-400 truncate">{s.mail}</div>
                                </div>
                              </div>
                            </td>

                            {/* Preferences */}
                            <td className="px-4 py-3">
                              <div className="space-y-1 max-w-[280px]">
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-[10px] text-slate-400 mr-0.5">{t('subscribers.topicsLabel')}</span>
                                  {topicNames.length === 0 ? (
                                    <span className="text-[10px] text-slate-400 italic">—</span>
                                  ) : (
                                    <>
                                      {topicNames.slice(0, 3).map((name, i) => (
                                        <Badge key={name} className={`text-[10px] border font-medium px-1.5 py-0 ${subTopics[i]?.color ?? TOPIC_COLOR_PALETTE[i % TOPIC_COLOR_PALETTE.length]}`}>
                                          {name}
                                        </Badge>
                                      ))}
                                      {topicNames.length > 3 && <span className="text-[10px] text-slate-500">+{topicNames.length - 3}</span>}
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-[10px] text-slate-400 mr-0.5">{t('common.buzzwords')}:</span>
                                  {allBuzz.length === 0 ? (
                                    <span className="text-[10px] text-slate-400 italic">—</span>
                                  ) : (
                                    <>
                                      {allBuzz.slice(0, 3).map((bw, i) => (
                                        <Badge key={bw} className={`text-[10px] border font-medium px-1.5 py-0 ${BUZZWORD_COLORS[i % BUZZWORD_COLORS.length]}`}>
                                          {bw}
                                        </Badge>
                                      ))}
                                      {allBuzz.length > 3 && <span className="text-[10px] text-slate-500">+{allBuzz.length - 3}</span>}
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-[10px] text-slate-400 mr-0.5">{t('common.keywords')}:</span>
                                  {allKeywords.length === 0 ? (
                                    <span className="text-[10px] text-slate-400 italic">—</span>
                                  ) : (
                                    <>
                                      {allKeywords.slice(0, 3).map((kw, i) => (
                                        <Badge key={kw} className={`text-[10px] border font-medium px-1.5 py-0 ${KEYWORD_COLORS[i % KEYWORD_COLORS.length]}`}>
                                          {kw}
                                        </Badge>
                                      ))}
                                      {allKeywords.length > 3 && <span className="text-[10px] text-slate-500">+{allKeywords.length - 3}</span>}
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Delivery */}
                            <td className="px-4 py-3">
                              {sched ? (
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-1.5 text-slate-700 text-xs font-medium">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    {t('subscribers.dailyAt', { time: `${sched.hour}:${sched.minute}`, tz: tzAbbr(sched.timezone) })}
                                  </div>
                                  <div className="text-[11px] text-slate-400">{t('subscribers.emailDigest')}</div>
                                  <div className="text-[11px] text-green-600">{t('subscribers.lastDeliverySuccess')}</div>
                                </div>
                              ) : (
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                    <Clock className="w-3 h-3" /> {t('subscribers.notScheduled')}
                                  </div>
                                  <div className="text-[11px] text-slate-400">{t('subscribers.emailDigest')}</div>
                                  <div className="text-[11px] text-slate-400">{t('subscribers.lastDeliveryNa')}</div>
                                </div>
                              )}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                status === 'Active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {status === 'Active' ? t('common.active') : t('common.inactive')}
                              </span>
                            </td>

                            {/* Last Sent */}
                            <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                              {formatLastSent(s.last_sent_at, localeTag, t('common.never'))}
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  title={t('subscribers.importTopicsTitle')}
                                  aria-label={t('subscribers.importTopicsAria')}
                                  disabled={importingSubId === s.id}
                                  onClick={(e) => { e.stopPropagation(); handleImportClick(s.id); }}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {importingSubId === s.id
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <FileUp className="w-4 h-4" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSelectRow(s); }}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                    {filtered.length === 0 && !loading && (
                      <tr>
                        <td colSpan={6} className="px-6 py-16 text-center text-slate-500 text-sm">
                          {subscribers.length === 0
                            ? t('subscribers.emptyNone')
                            : t('subscribers.emptyFilters')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filtered.length > 0 && (
              <div className={`flex shrink-0 items-center justify-between border-t border-white/35 px-6 py-3 ${LIQUID_GLASS_SOFT}`}>
                <span className="text-xs text-slate-500">
                  {t('subscribers.showingSubscribers', { from: showStart, to: showEnd, total: filtered.length })}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors disabled:cursor-not-allowed disabled:opacity-40 hover:bg-white/50 ${GLASS_FIELD}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {pageNumbers.map((pn, idx) =>
                    pn === 'dots' ? (
                      <span key={`dots-${idx}`} className="flex h-8 w-8 items-center justify-center text-xs text-slate-400">…</span>
                    ) : (
                      <button
                        key={pn}
                        type="button"
                        onClick={() => setPage(pn)}
                        className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                          pn === safePage
                            ? 'bg-blue-600/90 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.6)] ring-1 ring-white/30'
                            : `${GLASS_FIELD} text-slate-600 hover:bg-white/55`
                        }`}
                      >
                        {pn}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors disabled:cursor-not-allowed disabled:opacity-40 hover:bg-white/50 ${GLASS_FIELD}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <select
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                  className={`cursor-pointer rounded-lg px-2 py-1.5 text-xs text-slate-600 ${GLASS_FIELD}`}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{t('subscribers.perPage', { n })}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Right Sidebar ─── */}
      <AnimatePresence>
        {selectedSubLive && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 360, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex min-h-0 shrink-0 flex-col overflow-hidden border-l border-white/40 ${LIQUID_GLASS_PANEL}`}
          >
            <div className="h-full min-h-0 w-[360px] overflow-y-auto overscroll-contain">
              {/* Sidebar header */}
              <div className={`flex items-start justify-between border-b border-white/35 px-6 pb-4 pt-6 ${LIQUID_GLASS_SOFT}`}>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{selectedSubLive.username || selectedSubLive.mail}</h2>
                  <p className="text-sm text-slate-500">{t('subscribers.sidebarDetails')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSub(null)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-6">
                {/* Profile */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">{t('common.profile')}</h3>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => openEdit(selectedSubLive)}>
                      <Pencil className="w-3 h-3" /> {t('common.edit')}
                    </Button>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.email')}</span>
                      <span className="text-slate-800 font-medium truncate ml-4">{selectedSubLive.mail}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.language')}</span>
                      <Badge variant="secondary" className="text-[10px] font-semibold px-1.5 py-0">
                        {langLabel(selectedSubLive.language)}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.status')}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        getStatus(selectedSubLive.id) === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {getStatus(selectedSubLive.id) === 'Active' ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.memberSince')}</span>
                      <span className="text-slate-800">—</span>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* Topics — Buzzwords & Keywords */}
                <section>
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">{t('subscribers.topicsSectionTitle')}</h3>
                      <p className="mt-0.5 text-[11px] text-slate-400">
                        {t('subscribers.topicsSectionDesc')}
                      </p>
                    </div>
                    {selectedSubLive && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 shrink-0 text-[10px]"
                        onClick={() => navigate(`/subscribers/${selectedSubLive.id}`)}
                      >
                        {t('subscribers.fullEditor')}
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {sidebarTopics.length === 0 ? (
                      <p className="text-xs italic text-slate-400">{t('subscribers.noTopicsHint')}</p>
                    ) : (
                      sidebarTopics.map((topic) => {
                        const buzzFieldKey = buzzwordFieldKey(selectedSubLive!.id, topic.id);
                        const kwFieldKey = keywordFieldKey(selectedSubLive!.id, topic.id);
                        return (
                          <div key={topic.id} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                            <Badge className={`border text-[10px] font-medium ${topic.color}`}>{topic.name}</Badge>

                            <p className="mt-2 text-[10px] font-medium text-slate-500">{t('common.buzzwords')}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {topic.buzzwords.length === 0 ? (
                                <span className="text-[11px] italic text-slate-400">{t('subscribers.noBuzzwordsYet')}</span>
                              ) : (
                                topic.buzzwords.map((word, index) => (
                                  <span
                                    key={word}
                                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${BUZZWORD_COLORS[index % BUZZWORD_COLORS.length]}`}
                                  >
                                    #{word}
                                    <button
                                      type="button"
                                      disabled={wordsSaving}
                                      onClick={() => void handleRemoveBuzzword(selectedSubLive!.id, topic.id, word)}
                                      className="text-slate-400 transition-colors hover:text-red-500"
                                      aria-label={t('subscribers.removeBuzzwordAria', { word })}
                                    >
                                      <X className="h-2.5 w-2.5" />
                                    </button>
                                  </span>
                                ))
                              )}
                            </div>
                            <div className="mt-2 flex gap-1.5">
                              <div className="relative min-w-0 flex-1">
                                <Hash className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                                <Input
                                  placeholder={t('subscribers.addBuzzwordPlaceholder')}
                                  value={newBuzzwordByTopicKey[buzzFieldKey] ?? ''}
                                  onChange={(event) =>
                                    setNewBuzzwordByTopicKey((prev) => ({
                                      ...prev,
                                      [buzzFieldKey]: event.target.value,
                                    }))
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') void handleAddBuzzword(selectedSubLive!.id, topic.id);
                                  }}
                                  className="h-8 pl-7 text-xs"
                                  disabled={wordsSaving}
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2.5"
                                disabled={wordsSaving || !(newBuzzwordByTopicKey[buzzFieldKey] ?? '').trim()}
                                onClick={() => void handleAddBuzzword(selectedSubLive!.id, topic.id)}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>

                            <p className="mt-3 text-[10px] font-medium text-slate-500">{t('common.keywords')}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {topic.keywords.length === 0 ? (
                                <span className="text-[11px] italic text-slate-400">{t('subscribers.noKeywordsYet')}</span>
                              ) : (
                                topic.keywords.map((word, index) => (
                                  <span
                                    key={word}
                                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${KEYWORD_COLORS[index % KEYWORD_COLORS.length]}`}
                                  >
                                    #{word}
                                    <button
                                      type="button"
                                      disabled={wordsSaving}
                                      onClick={() => void handleRemoveKeyword(selectedSubLive!.id, topic.id, word)}
                                      className="text-slate-400 transition-colors hover:text-red-500"
                                      aria-label={t('subscribers.removeKeywordAria', { word })}
                                    >
                                      <X className="h-2.5 w-2.5" />
                                    </button>
                                  </span>
                                ))
                              )}
                            </div>
                            <div className="mt-2 flex gap-1.5">
                              <div className="relative min-w-0 flex-1">
                                <KeyRound className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                                <Input
                                  placeholder={t('subscribers.addKeywordPlaceholder')}
                                  value={newKeywordByTopicKey[kwFieldKey] ?? ''}
                                  onChange={(event) =>
                                    setNewKeywordByTopicKey((prev) => ({
                                      ...prev,
                                      [kwFieldKey]: event.target.value,
                                    }))
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') void handleAddKeyword(selectedSubLive!.id, topic.id);
                                  }}
                                  className="h-8 pl-7 text-xs"
                                  disabled={wordsSaving}
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2.5"
                                disabled={wordsSaving || !(newKeywordByTopicKey[kwFieldKey] ?? '').trim()}
                                onClick={() => void handleAddKeyword(selectedSubLive!.id, topic.id)}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    )}

                    <div className="rounded-lg border border-dashed border-slate-200 p-3">
                      <p className="mb-2 text-[11px] font-medium text-slate-500">{t('subscribers.addTopic')}</p>
                      <div className="flex gap-1.5">
                        <Input
                          placeholder={t('subscribers.topicNamePlaceholder')}
                          value={newSidebarTopicName}
                          onChange={(event) => setNewSidebarTopicName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') void handleAddSidebarTopic(selectedSubLive!.id);
                          }}
                          className="h-8 text-xs"
                          disabled={wordsSaving}
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-blue-600 px-2.5 text-white hover:bg-blue-700"
                          disabled={wordsSaving || !newSidebarTopicName.trim()}
                          onClick={() => void handleAddSidebarTopic(selectedSubLive!.id)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* Delivery Settings */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">{t('subscribers.deliverySettings')}</h3>
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.frequency')}</span>
                      <span className="text-slate-800">{sidebarSchedule ? t('common.daily') : '—'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('subscribers.sendTime')}</span>
                      <span className="text-slate-800">{sidebarSchedule ? `${sidebarSchedule.hour}:${sidebarSchedule.minute}` : '—'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('common.timezone')}</span>
                      <span className="text-slate-800">{sidebarSchedule ? `${tzAbbr(sidebarSchedule.timezone)} (${sidebarSchedule.timezone || 'Europe/Berlin'})` : '—'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('subscribers.emailTemplate')}</span>
                      <span className="text-slate-800">{t('subscribers.defaultDigest')}</span>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* Delivery History */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">{t('subscribers.deliveryHistory')}</h3>
                  <div className="space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('subscribers.lastSent')}</span>
                      <span className="text-slate-800">{formatLastSent(selectedSubLive?.last_sent_at, localeTag, t('common.never'))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('subscribers.lastStatus')}</span>
                      {selectedSubLive?.last_sent_at ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{t('common.success')}</span>
                      ) : (
                        <span className="text-slate-800">—</span>
                      )}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('subscribers.lastError')}</span>
                      <span className="text-slate-800">—</span>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* Actions */}
                <div className="space-y-2.5">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5 text-xs" onClick={() => showSuccess(t('subscribers.toastTestEmailSoon'))}>
                      <Mail className="w-3.5 h-3.5" /> {t('subscribers.sendTestEmail')}
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5 text-xs text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => showSuccess(t('subscribers.toastPauseSoon'))}>
                      <Pause className="w-3.5 h-3.5" /> {t('subscribers.pauseSubscriber')}
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5 text-xs text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => { setDeleteTarget(selectedSubLive); }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {t('subscribers.deleteSubscriber')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ═══════════ Dialogs ═══════════ */}

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-blue-600" /> {t('subscribers.editTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">{t('common.email')}</Label>
              <Input id="edit-email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-username">{t('common.username')}</Label>
              <Input id="edit-username" value={editUsername} onChange={(e) => setEditUsername(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-language">{t('subscribers.emailLanguage')}</Label>
              <Select value={editLanguage} onValueChange={(v) => setEditLanguage(v as LanguageCode)}>
                <SelectTrigger id="edit-language" className="w-full gap-2 bg-white">
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <LanguageFlagByCode code={editLanguage} className="h-4 w-4" />
                    <span className="truncate">{LANGUAGE_OPTIONS[editLanguage].countryLabel}</span>
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <LanguageFlagByCode code={code} className="h-4 w-4" />
                        <span>{LANGUAGE_OPTIONS[code].countryLabel}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-new-password">{t('subscribers.newPasswordOptional')}</Label>
              <div className="relative">
                <Input
                  id="edit-new-password"
                  type={editPasswordVisible ? 'text' : 'password'}
                  value={editNewPassword}
                  onChange={(e) => setEditNewPassword(e.target.value)}
                  placeholder={t('subscribers.passwordKeepCurrent')}
                  className="pr-10 font-mono"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setEditPasswordVisible((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                >
                  {editPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">{t('subscribers.passwordMinHint')}</p>
            </div>
          </div>
          <Separator />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" disabled={saving}>{t('common.cancel')}</Button></DialogClose>
            <Button onClick={() => void saveEdit()} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? t('common.saving') : t('emailTemplates.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-4 h-4" /> {t('subscribers.deleteTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 py-2">
            {t('subscribers.deleteDesc', { email: deleteTarget?.mail ?? '' })}
          </p>
          <Separator />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" disabled={saving}>{t('common.cancel')}</Button></DialogClose>
            <Button onClick={() => void confirmDelete()} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? t('common.removing') : t('common.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-blue-600" /> {t('subscribers.addTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">{t('common.email')}</Label>
              <Input id="new-email" type="email" placeholder={t('subscribers.emailPlaceholder')} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-username">{t('common.username')}</Label>
              <Input id="new-username" placeholder={t('subscribers.usernamePlaceholder')} value={newUsername} onChange={(e) => setNewUsername(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-language">{t('subscribers.emailLanguage')}</Label>
              <Select value={newLanguage} onValueChange={(v) => setNewLanguage(v as LanguageCode)}>
                <SelectTrigger id="new-language" className="w-full gap-2 bg-white">
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <LanguageFlagByCode code={newLanguage} className="h-4 w-4" />
                    <span className="truncate">{LANGUAGE_OPTIONS[newLanguage].countryLabel}</span>
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <LanguageFlagByCode code={code} className="h-4 w-4" />
                        <span>{LANGUAGE_OPTIONS[code].countryLabel}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{t('common.password')}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={newPasswordVisible ? 'text' : 'password'}
                  placeholder={t('subscribers.passwordPlaceholder')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10 font-mono"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setNewPasswordVisible((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                >
                  {newPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <Separator />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" disabled={saving}>{t('common.cancel')}</Button></DialogClose>
            <Button
              onClick={() => void handleAdd()}
              disabled={saving || !newEmail.trim() || !newUsername.trim() || !newPassword.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {saving ? t('common.adding') : t('subscribers.addSubscriber')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
